import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import moment from 'moment';
import { orderBy, get } from 'lodash';
import { DataTable } from 'web-ui-components/lib/molecules/tables';
import { Button } from 'web-ui-components/lib/atoms/buttons';
import { getActiveSortOrder, getAllActiveMandates, getFilteredResults } from '../../../states/managePayments/selectors';
import {
	dateFormat,
	MANDATE_TYPE_SCHEDULE_PAYMENT,
	MANDATE_TYPE_STANDING_ORDER,
	MANDATE_FILTER_STANDING_ORDERS,
	MANDATE_FILTER_DIRECT_DEBITS,
	MANDATE_FILTER_OTHER_SCHEDULE,
	MANDATE_FILTER_ALL,
	MANDATE_TYPE_SCHEDULE_TRANSFER,
	MANDATE_TYPE_IFT,
	MANDATE_TYPE_CHAPS,
	MANDATE_PAGE_SIZE,
} from '../../../config';
import { formatFrom, formatTo, amountFormater, activePaymentsColumn, showFileUploadIcon } from './ColumnFormater';
import { calcPaymentTypeCapable } from '../../../states/capabilities/selectors';

import withContent from '../../../containers/withContent';
import content, { contentPropTypes } from './PaymentListing.content';
import { setActiveSortOrder } from '../../../states/managePayments/actions';

const sortOrders = {
	desc: 'asc',
	asc: 'desc',
};
const mapState = (state) => ({
	activeMandates: getAllActiveMandates(state),
	isPaymentTypeCapable: (paymentType) => calcPaymentTypeCapable(state, paymentType),
	sortByColumn: getActiveSortOrder(state),
});
const mapDispatch = (dispatch) => ({
	setActiveSortOrder: (order) => dispatch(setActiveSortOrder(order)),
});

export const getOptions = (
	mandate,
	handleView,
	handleEdit,
	handleCancelView,
	{ viewButtonLabel, cancelButtonLabel, editButtonLabel },
	isPaymentTypeCapable
) => {
	const isEditEnabled =
		isPaymentTypeCapable(mandate.mandateType) &&
		(([MANDATE_TYPE_SCHEDULE_PAYMENT, MANDATE_TYPE_SCHEDULE_TRANSFER, MANDATE_TYPE_STANDING_ORDER].includes(
			mandate.mandateType
		) &&
			mandate.actions_available['/account/mandates/so/write']) ||
			[MANDATE_TYPE_IFT, MANDATE_TYPE_CHAPS].includes(mandate.mandateType));
	return (
		<div>
			<Button id={`view-button-${mandate.id}`} width="flush" size="xs" onClick={() => handleView(mandate)}>
				{viewButtonLabel}
			</Button>
			<Button id={`cancel-button-${mandate.id}`} width="flush" size="xs" onClick={() => handleCancelView(mandate)}>
				{cancelButtonLabel}
			</Button>
			{isEditEnabled && (
				<Button id={`edit-button-${mandate.id}`} width="flush" size="xs" onClick={() => handleEdit(mandate)}>
					{editButtonLabel}
				</Button>
			)}
		</div>
	);
};
export const getFormattedResult = (
	mandate,
	handleView,
	handleEdit,
	handleCancelView,
	content,
	isPaymentTypeCapable
) => ({
	...mandate,
	formatedFrom: formatFrom(mandate),
	options: getOptions(mandate, handleView, handleEdit, handleCancelView, content, isPaymentTypeCapable),
	formatedTo: formatTo(mandate),
	formatedAmount: amountFormater(mandate),
	formatedWhen: moment(mandate.when).format(dateFormat),
	paymentUploadIcon: showFileUploadIcon(mandate.isFileUploadBatch),
});
export const getSortedResults = (mandates, sortById, sortOrder) => orderBy(mandates, [sortById], [sortOrder]);

export const getNoResultsMsg = (selectedFilter, { noResultsAllPayments, noResultsDD, noResultsSO, noResultsOther }) => {
	const foooterMsgs = {
		[MANDATE_FILTER_ALL]: noResultsAllPayments,
		[MANDATE_FILTER_DIRECT_DEBITS]: noResultsDD,
		[MANDATE_FILTER_STANDING_ORDERS]: noResultsSO,
		[MANDATE_FILTER_OTHER_SCHEDULE]: noResultsOther,
	};
	return get(foooterMsgs, selectedFilter, '');
};

export const getFooterMsg = (
	selectedFilter,
	{ footerActiveAllListingMsg, footerActiveDDListMsg, footerActiveSOListMsg, footerActiveOtherListMsg }
) => {
	const foooterMsgs = {
		[MANDATE_FILTER_ALL]: footerActiveAllListingMsg,
		[MANDATE_FILTER_DIRECT_DEBITS]: footerActiveDDListMsg,
		[MANDATE_FILTER_STANDING_ORDERS]: footerActiveSOListMsg,
		[MANDATE_FILTER_OTHER_SCHEDULE]: footerActiveOtherListMsg,
	};
	return get(foooterMsgs, selectedFilter, '');
};

class ActivePaymentListing extends Component {
	constructor(props) {
		super(props);

		this.handleSortOrder = this.handleSortOrder.bind(this);
		this.getResultsToDisplay = this.getResultsToDisplay.bind(this);
	}

	getResultsToDisplay() {
		const {
			activeMandates,
			selectedFilter,
			handleView,
			handleEdit,
			handleCancelView,
			content,
			isPaymentTypeCapable,
			sortByColumn: { id, sortDirection },
			mandateActivePage,
			mandateActivePageTotal,
		} = this.props;

		const filteredResult = getSortedResults(getFilteredResults(activeMandates, selectedFilter), id, sortDirection);

		const formattedResult = filteredResult.map((mandate) =>
			getFormattedResult(mandate, handleView, handleEdit, handleCancelView, content, isPaymentTypeCapable)
		);

		if (mandateActivePageTotal > 1) {
			return formattedResult.slice((mandateActivePage - 1) * MANDATE_PAGE_SIZE, mandateActivePage * MANDATE_PAGE_SIZE);
		}

		return formattedResult;
	}

	handleSortOrder(column) {
		const sortByColumn = {
			...column,
			sortDirection: sortOrders[column.sortDirection],
		};
		this.props.setActiveSortOrder(sortByColumn);
		this.props.persistSortOrder(sortByColumn, true);
	}

	render() {
		const {
			hasError,
			hasApiCallInProgress,
			selectedFilter,
			content,
			renderRetryComponent,
			sortByColumn,
			mandateActivePage,
			mandateActivePageTotal,
		} = this.props;

		const results = hasError || hasApiCallInProgress ? [] : this.getResultsToDisplay();
		const columns = activePaymentsColumn(sortByColumn);
		const isPaginated = mandateActivePageTotal > 1;
		const isLastPage = !isPaginated || (isPaginated && mandateActivePage === mandateActivePageTotal);

		return (
			<div id="scheduled-payment__active">
				{results.length > 0 && (
					<DataTable
						breakpointSnap="1280"
						isBranded
						layout="transactions"
						columns={columns}
						data={results}
						keyPath={['id']}
						onHeaderCellClick={this.handleSortOrder}
					/>
				)}
				{!results.length && !hasApiCallInProgress && (
					<div className="u-padding-2 u-text-align-center">{getNoResultsMsg(selectedFilter, content)}</div>
				)}
				{hasError && renderRetryComponent()}

				{isLastPage && (
					<div id="footer-msg" className="u-padding-2 u-text-align-center">
						{getFooterMsg(selectedFilter, content)}
					</div>
				)}
			</div>
		);
	}
}
ActivePaymentListing.propTypes = {
	selectedFilter: PropTypes.string.isRequired,
	activeMandates: PropTypes.shape([{}]),
	content: PropTypes.shape(contentPropTypes),
	hasError: PropTypes.bool.isRequired,
	hasApiCallInProgress: PropTypes.bool.isRequired,
	handleView: PropTypes.func.isRequired,
	handleEdit: PropTypes.func.isRequired,
	handleCancelView: PropTypes.func.isRequired,
	renderRetryComponent: PropTypes.func.isRequired,
	isPaymentTypeCapable: PropTypes.func.isRequired,
	sortByColumn: PropTypes.shape({}),
	setActiveSortOrder: PropTypes.func,
	mandateActivePage: PropTypes.number.isRequired,
	mandateActivePageTotal: PropTypes.number.isRequired,
	persistSortOrder: PropTypes.func.isRequired,
};
export const connectWrapper = connect(mapState, mapDispatch)(ActivePaymentListing);
export default withContent({ content })(connectWrapper);
